package com.islamicmedia.site.components

import kotlinx.browser.document
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import kotlin.math.PI
import kotlin.math.floor
import kotlin.math.min
import kotlin.math.sin

// The Flappy game's sky, the same design as mobile's FlappySky.kt. It moves from day to rain to sunset to
// night as the score climbs, then a new day starts. The sprites are LottieFiles free animations
// (/animations/sky_*.json, the same files as mobile), and every layer scrolls at its own speed.

/** Points per sky phase: day at 0, rain at 3, sunset at 6, night at 9, day again at 12. */
internal const val SKY_POINTS_PER_PHASE = 3
/** How fast the sky blends into the next phase, in phases per second. */
internal const val SKY_BLEND_SPEED = 0.7
/** How far the sky drifts per 60fps frame before the game starts, in px. */
internal const val SKY_IDLE_DRIFT = 0.35
private const val SKY_FPS = 30.0 // the sprites are redrawn at most this often

/** A LottieFiles animation that lottie-web draws into its own small canvas, to be copied onto the game. */
private class SkySprite(private val path: String, width: Int, height: Int) {
    val canvas = (document.createElement("canvas") as HTMLCanvasElement).also {
        it.width = width
        it.height = height
    }
    var anim: dynamic = null
    private var shownFrame = -1.0

    fun load(lottie: dynamic) {
        if (anim != null) return
        val settings: dynamic = js("({})")
        settings.context = canvas.getContext("2d")
        settings.clearCanvas = true
        val options: dynamic = js("({})")
        options.renderer = "canvas"
        options.loop = true
        options.autoplay = false
        options.path = path
        options.rendererSettings = settings
        anim = lottie.loadAnimation(options)
    }

    /** Moves the animation to [time] seconds. False until it has loaded. */
    fun ready(time: Double): Boolean {
        val a = anim
        if (a == null || a.isLoaded != true) return false
        val frames = (a.totalFrames as Number).toDouble()
        val rate = (a.frameRate as Number).toDouble()
        val frame = (floor(time * SKY_FPS) / SKY_FPS * rate) % frames
        if (frame != shownFrame) {
            a.goToAndStop(frame, true)
            shownFrame = frame
        }
        return true
    }
}

/** One phase of the sky. Colours are 0xRRGGBB, amounts 0..1; [sunY] is the sun's centre as a share of the height. */
private class SkyLook(
    val top: Int,
    val bottom: Int,
    val skyline: Int,
    val sun: Double,
    val sunY: Double,
    val moon: Double,
    val stars: Double,
    val clouds: Double,
    val cloudShade: Double,
    val rain: Double,
    val birds: Double,
    val plane: Double,
)

private val SKY_LOOKS = listOf(
    // Day
    SkyLook(0x4FB3F2, 0xCDEBFF, 0x8FC4E8, sun = 1.0, sunY = 0.2, moon = 0.0, stars = 0.0,
        clouds = 1.0, cloudShade = 0.0, rain = 0.0, birds = 1.0, plane = 1.0),
    // Rain
    SkyLook(0x6F8197, 0xAEBCCB, 0x8593A5, sun = 0.0, sunY = 0.2, moon = 0.0, stars = 0.0,
        clouds = 1.0, cloudShade = 0.35, rain = 1.0, birds = 0.0, plane = 0.6),
    // Sunset
    SkyLook(0xE8738A, 0xFFC48A, 0xC0727F, sun = 1.0, sunY = 0.55, moon = 0.0, stars = 0.15,
        clouds = 0.9, cloudShade = 0.1, rain = 0.0, birds = 1.0, plane = 0.0),
    // Night
    SkyLook(0x0B1A36, 0x27407A, 0x16284F, sun = 0.0, sunY = 1.0, moon = 1.0, stars = 1.0,
        clouds = 0.35, cloudShade = 0.55, rain = 0.0, birds = 0.0, plane = 0.0),
)

/** The sky at [pos] phases in (any number; 4 is the next day), eased between the two nearest phases. */
private fun skyLookAt(pos: Double): SkyLook {
    val base = floor(pos)
    val a = SKY_LOOKS[posMod(base, SKY_LOOKS.size.toDouble()).toInt()]
    val b = SKY_LOOKS[posMod(base + 1, SKY_LOOKS.size.toDouble()).toInt()]
    val t = (pos - base).let { it * it * (3 - 2 * it) }
    fun mix(x: Double, y: Double) = x + (y - x) * t
    return SkyLook(
        top = mixColor(a.top, b.top, t), bottom = mixColor(a.bottom, b.bottom, t), skyline = mixColor(a.skyline, b.skyline, t),
        sun = mix(a.sun, b.sun), sunY = mix(a.sunY, b.sunY), moon = mix(a.moon, b.moon), stars = mix(a.stars, b.stars),
        clouds = mix(a.clouds, b.clouds), cloudShade = mix(a.cloudShade, b.cloudShade), rain = mix(a.rain, b.rain),
        birds = mix(a.birds, b.birds), plane = mix(a.plane, b.plane),
    )
}

/** A cloud: left edge at [x] canvas widths, top at [y] heights, [size] heights wide, moving at [parallax]. */
private class Cloud(val x: Double, val y: Double, val size: Double, val parallax: Double, val alpha: Double)

private val FAR_CLOUDS = listOf(Cloud(0.05, -0.05, 0.42, 0.05, 0.7), Cloud(0.55, 0.08, 0.36, 0.05, 0.7))
private val NEAR_CLOUDS = listOf(Cloud(0.3, 0.22, 0.6, 0.14, 1.0), Cloud(0.85, -0.12, 0.55, 0.14, 1.0))

/** Stars as (x, y) shares of the canvas, in the top part of the sky. */
private val STARS = listOf(
    0.06 to 0.10, 0.14 to 0.32, 0.22 to 0.06, 0.31 to 0.22, 0.38 to 0.44, 0.45 to 0.12,
    0.52 to 0.30, 0.60 to 0.05, 0.66 to 0.40, 0.74 to 0.16, 0.83 to 0.48, 0.90 to 0.08,
    0.95 to 0.28, 0.27 to 0.52, 0.58 to 0.55,
)

internal class FlappySky {
    private val sun = SkySprite("/animations/sky_sun.json", 200, 200)
    private val moon = SkySprite("/animations/sky_moon.json", 240, 240)
    private val clouds = SkySprite("/animations/sky_clouds.json", 360, 360)
    private val rain = SkySprite("/animations/sky_rain.json", 621, 210)
    private val birds = SkySprite("/animations/sky_birds.json", 200, 200)
    private val plane = SkySprite("/animations/sky_plane.json", 200, 200)
    private val shootingStar = SkySprite("/animations/sky_shooting_star.json", 320, 320)
    private val sprites = listOf(sun, moon, clouds, rain, birds, plane, shootingStar)

    fun load(lottie: dynamic) = sprites.forEach { it.load(lottie) }

    fun destroy() = sprites.forEach { it.anim?.destroy() }

    /**
     * Draws the whole sky behind the game. [pos] is the phase (see [skyLookAt]), [scroll] how many px the
     * world has moved, and [time] seconds since the page opened (it never resets).
     */
    fun draw(ctx: CanvasRenderingContext2D, w: Double, h: Double, pos: Double, scroll: Double, time: Double) {
        val look = skyLookAt(pos)

        val gradient = ctx.createLinearGradient(0.0, 0.0, 0.0, h)
        gradient.addColorStop(0.0, css(look.top))
        gradient.addColorStop(1.0, css(look.bottom))
        ctx.fillStyle = gradient
        ctx.fillRect(0.0, 0.0, w, h)

        if (look.stars > 0.01) {
            ctx.fillStyle = "#FFFFFF"
            STARS.forEachIndexed { i, (x, y) ->
                val twinkle = 0.55 + 0.45 * sin(time * 2 + i * 1.7)
                ctx.globalAlpha = min(look.stars * twinkle, 1.0)
                ctx.beginPath()
                ctx.arc(x * w, y * h, 1.6 * (0.7 + (i % 3) * 0.3), 0.0, 2 * PI)
                ctx.fill()
            }
            ctx.globalAlpha = 1.0
        }
        // The moon sits on the left so it never overlaps the sun (setting and rising on the right).
        drawSprite(ctx, moon, w * 0.25 - h * 0.19, h * 0.04, h * 0.38, look.moon, time)
        if (look.stars > 0.5) drawSprite(ctx, shootingStar, w * 0.55, -h * 0.08, h * 0.55, (look.stars - 0.5) * 2, time)
        drawSprite(ctx, sun, w * 0.8 - h * 0.17, h * (look.sunY - 0.17), h * 0.34, look.sun, time)

        for (c in FAR_CLOUDS) drawCloud(ctx, c, w, h, scroll, look, time)

        // Far-off flyers, each on its own timetable so they don't arrive together.
        val birdT = time % 20
        if (birdT < 15) {
            val size = h * 0.32
            val p = birdT / 15
            drawSprite(ctx, birds, w - p * (w + size), h * (0.26 + 0.02 * sin(time * 1.3)), size, look.birds * 0.8, time)
        }
        val planeT = (time + 9) % 26
        if (planeT < 13) {
            val size = h * 0.3
            val p = planeT / 13
            drawSprite(ctx, plane, -size + p * (w + size), h * (0.06 - 0.08 * p), size, look.plane, time)
        }

        drawSkyline(ctx, w, h, look.skyline, scroll)

        for (c in NEAR_CLOUDS) drawCloud(ctx, c, w, h, scroll, look, time)

        // The rain animation is a 414x140 strip; stretched over the canvas its streaks just fall longer.
        if (look.rain > 0.01 && rain.ready(time)) {
            ctx.globalAlpha = min(look.rain, 1.0)
            ctx.drawImage(rain.canvas, 0.0, 0.0, w, h)
            ctx.globalAlpha = 1.0
        }
    }

    private fun drawCloud(ctx: CanvasRenderingContext2D, c: Cloud, w: Double, h: Double, scroll: Double, look: SkyLook, time: Double) {
        val width = c.size * h
        val period = w + width
        val left = posMod(c.x * w - scroll * c.parallax + width, period) - width
        drawSprite(ctx, clouds, left, c.y * h, width, c.alpha * look.clouds, time, look.cloudShade)
    }
}

/** Draws [sprite] [width] wide with its own aspect ratio, darkened by [shade] (0..1). Nothing until it has loaded. */
private fun drawSprite(
    ctx: CanvasRenderingContext2D, sprite: SkySprite, left: Double, top: Double, width: Double,
    alpha: Double, time: Double, shade: Double = 0.0
) {
    if (alpha <= 0.01 || !sprite.ready(time)) return
    ctx.globalAlpha = min(alpha, 1.0)
    if (shade > 0.01) ctx.asDynamic().filter = "brightness(${1 - shade * 0.5})"
    ctx.drawImage(sprite.canvas, left, top, width, width * sprite.canvas.height / sprite.canvas.width)
    ctx.asDynamic().filter = "none"
    ctx.globalAlpha = 1.0
}

/** A row of far-off domes and minarets along the bottom, repeating every 1.5 heights. */
private fun drawSkyline(ctx: CanvasRenderingContext2D, w: Double, h: Double, color: Int, scroll: Double) {
    val tile = 1.5 * h
    ctx.fillStyle = css(color)
    ctx.beginPath()
    var left = -posMod(scroll * 0.25, tile)
    while (left < w) {
        fun box(l: Double, top: Double, r: Double) = ctx.rect(left + l * h, h - top * h, (r - l) * h, top * h)
        fun dome(cx: Double, base: Double, radius: Double) {
            ctx.moveTo(left + (cx + radius) * h, h - base * h)
            ctx.arc(left + cx * h, h - base * h, radius * h, 0.0, 2 * PI)
        }
        fun minaret(l: Double, r: Double, top: Double, spire: Double) {
            box(l, top, r)
            ctx.moveTo(left + (l - 0.005) * h, h - top * h)
            ctx.lineTo(left + (l + r) / 2 * h, h - (top + spire) * h)
            ctx.lineTo(left + (r + 0.005) * h, h - top * h)
            ctx.closePath()
        }
        box(0.0, 0.10, 0.20)
        minaret(0.24, 0.275, 0.26, 0.05)
        box(0.30, 0.12, 0.64)
        dome(0.47, 0.12, 0.11)
        box(0.467, 0.27, 0.473)
        dome(0.355, 0.12, 0.045)
        dome(0.585, 0.12, 0.045)
        minaret(0.68, 0.715, 0.30, 0.05)
        box(0.76, 0.08, 0.98)
        box(0.98, 0.14, 1.14)
        dome(1.06, 0.14, 0.06)
        box(1.18, 0.07, 1.5)
        left += tile
    }
    ctx.fill()
}

private fun mixColor(a: Int, b: Int, t: Double): Int {
    fun channel(shift: Int): Int {
        val x = (a shr shift) and 0xFF
        val y = (b shr shift) and 0xFF
        return (x + (y - x) * t).toInt()
    }
    return (channel(16) shl 16) or (channel(8) shl 8) or channel(0)
}

private fun css(color: Int) = "rgb(${(color shr 16) and 0xFF},${(color shr 8) and 0xFF},${color and 0xFF})"

private fun posMod(a: Double, m: Double) = ((a % m) + m) % m
